import type {
  AdminMember,
  AdminMemberFunctionalActivity,
  AdminMemberPosition,
} from '../model';

export interface MemberOverflowDisplay {
  primary: string;
  extraCount: number;
  tooltipText: string;
}

export function buildOverflowDisplay(values: readonly string[]): MemberOverflowDisplay {
  const filtered = values.map((v) => v.trim()).filter((v) => v.length > 0);
  if (filtered.length === 0) {
    return { primary: '-', extraCount: 0, tooltipText: '' };
  }
  if (filtered.length === 1) {
    return { primary: filtered[0], extraCount: 0, tooltipText: '' };
  }
  const primary = filtered[0];
  const remaining = filtered.slice(1);
  const extraCount = remaining.length;
  const lines = remaining.slice(0, 5);
  let tooltipText = lines.join('\n');
  if (remaining.length > 5) {
    tooltipText += '\n...';
  }
  return { primary, extraCount, tooltipText };
}

export function collectDepartmentCodesFromPositions(
  positions: readonly AdminMemberPosition[]
): string[] {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const p of positions) {
    const code = p.department?.code?.trim();
    if (!code) {
      continue;
    }
    if (seen.has(code)) {
      continue;
    }
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

export function collectPositionNamesFromPositions(
  positions: readonly AdminMemberPosition[]
): string[] {
  return positions.map((p) => p.name?.trim() ?? '').filter((n) => n.length > 0);
}

export function collectFunctionalActivityNames(
  activities: readonly AdminMemberFunctionalActivity[]
): string[] {
  return activities.map((a) => a.name?.trim() ?? '').filter((n) => n.length > 0);
}

export function departmentOverflowFromMember(member: AdminMember): MemberOverflowDisplay {
  return buildOverflowDisplay(collectDepartmentCodesFromPositions(member.positions ?? []));
}

export function positionNamesOverflowFromMember(member: AdminMember): MemberOverflowDisplay {
  return buildOverflowDisplay(collectPositionNamesFromPositions(member.positions ?? []));
}

export function functionalActivitiesOverflowFromMember(
  member: AdminMember
): MemberOverflowDisplay {
  return buildOverflowDisplay(collectFunctionalActivityNames(member.functionalActivities ?? []));
}
